import React, { useEffect, useMemo, useState } from 'react';
import { camelCase } from 'lodash';
import { FormForm } from '../../../../../../components/Form/FormForm';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { useModal } from '../../../../../../components/Modal/PresentedModal';
import { FormSelect } from '../../../../../../components/Form/Select/FormSelect';
import { t } from '../../../../../../locales/translate';
import { useMutation } from '@apollo/client';
import { Liquid } from 'liquidjs';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { FormHtmlEditor } from '../../../../../../components/Form/form_html_editor/form_html_editor';
import { graphql } from '../../../../../../generated';
import { useSuspenseQuery } from '../../../../../../util/use_suspense_query';
import './edit_ticket_layout.scss';

const engine = new Liquid();

const GET_TICKET_LAYOUT_DETAILS = graphql(`
  query GetTicketLayoutDetails($id: ID!, $ticketVariant: EventTicketVariant!) {
    event(id: $id) {
      id
      ticketLayoutDetails(variant: $ticketVariant) {
        id
        ticketLayout
        html
        exampleValues
      }
    }
    eventTicketLayoutTypes
  }
`);

const EVENT_TICKET_LAYOUT_MUTATION = graphql(`
  mutation EventTicketLayout(
    $eventId: ID!
    $ticketVariant: EventTicketVariant!
    $ticketLayout: EventTicketLayout!
    $html: String
  ) {
    updateEventTicketLayout(
      input: { eventId: $eventId, ticketVariant: $ticketVariant, ticketLayout: $ticketLayout, html: $html }
    ) {
      event {
        id
        ticketLayout
        onlineTicketLayout
        localTicketLayout
        ticketLayoutDetails(variant: $ticketVariant) {
          id
          ticketLayout
          html
          exampleValues
        }
      }
    }
  }
`);

const previewErrorHtml = `<div style="color: red; padding: 1em;">${t.page.event.design.ticketLayouts.edit.previewError}</div>`;

interface Props {
  eventId: string;
  ticketVariant: 'ONLINE' | 'LOCAL' | 'HARD';
}
export function EditTicketLayoutModal({ eventId, ticketVariant }: Props): JSX.Element {
  const { open, Modal, size, close } = useModal();

  const { data } = useSuspenseQuery(GET_TICKET_LAYOUT_DETAILS, {
    variables: { id: eventId, ticketVariant: ticketVariant },
  });

  const [updateEventTicketLayout] = useMutation(EVENT_TICKET_LAYOUT_MUTATION);

  const ticketLayoutOptions = useMemo(() => {
    return (
      data.eventTicketLayoutTypes?.map((variantType) => ({
        value: variantType,
        label: variantType.toLowerCase().replace(/_/g, '-'),
      })) || []
    );
  }, [JSON.stringify(data.eventTicketLayoutTypes)]);

  const { model, fields, onSubmit, dirty, valid, submissionStatus } = useForm({
    model: {
      ticketLayout: data.event.ticketLayoutDetails.ticketLayout,
      html: data.event.ticketLayoutDetails.html,
    },
    onSubmit: async ({ model }) => {
      await updateEventTicketLayout({
        variables: {
          eventId,
          ticketVariant,
          ticketLayout: model.ticketLayout,
          html: model.html,
        },
      });
      close();
    },
  });

  const [renderedHtml, setRenderedHtml] = useState('');

  useEffect(() => {
    engine
      .parseAndRender(model.html, data.event.ticketLayoutDetails.exampleValues)
      .then((html) => {
        setRenderedHtml(html);
      })
      .catch(() => {
        setRenderedHtml(previewErrorHtml);
      });
  }, [model.html, data.event.ticketLayoutDetails.exampleValues]);

  return (
    <Modal
      open={open}
      title={t.page.event.design.ticketLayouts.edit.title[camelCase(ticketVariant.toLowerCase())]}
      size={size}
    >
      <FormForm
        onSubmit={onSubmit}
        onClose={close}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
      >
        <FormSelect options={ticketLayoutOptions} {...fields.ticketLayout} />
        <Collapse open={model.ticketLayout === 'CUSTOM'}>
          <div className="edit-ticket-layout__editor">
            <div className="edit-ticket-layout__code">
              <FormHtmlEditor
                {...fields.html}
                label={t.page.event.design.ticketLayouts.edit.customHtml}
                height={'40vh'}
                liquidVariables={Object.keys(data.event.ticketLayoutDetails.exampleValues)}
              />
            </div>
            <div className="edit-ticket-layout__preview">
              <iframe className="edit-ticket-layout__iframe" srcDoc={renderedHtml} />
            </div>
          </div>
        </Collapse>
      </FormForm>
    </Modal>
  );
}
